import { Component, inject, signal, computed, input, output } from '@angular/core';
import {
  AlertService,
  CardComponent,
  CardContentComponent,
  IconComponent,
  LoadingComponent,
} from '@gains-com/ui-angular';
import { PurchaseDialogHeaderComponent } from '../purchase-dialog-header/purchase-dialog-header.component.js';
import { MatOption } from '@angular/material/autocomplete';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { QRCodeComponent } from 'angularx-qrcode';
import { CurrencyId, PaymentMethodEntityTypeId, UserBlockchainAddressFormatId } from '@gains-com/shared';
import { PaymentMethodAndUserBlockchainAddress, PaymentMethodsService } from '../../../../core/payment-methods.js';
import { ExchangeRatesService } from '../../../../core/exchange-rates.service.js';
import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, finalize, from, switchMap, tap } from 'rxjs';
import { PurchaseReceiptComponent } from '../purchase-receipt/purchase-receipt.component.js';
import { isModeRestrictedError, isPurchaseLimitReachedError, PurchaseSelection, PurchasePaymentMethod } from '../../common.js';
import { LOGGER_FACTORY } from '../../../../core/logger/logger-factory.provider.js';
import { Analytics } from '../../../../core/analytics/analytics.service.js';
import { UserViewService } from '../../../../core/view-states/user-view.service.js';
import { ActivePurchaseViewService } from '../../../../core/view-states/active-purchase-view.service.js';
import { CurrenciesViewService } from '../../../../core/view-states/currencies-view.service.js';
import { CurrencyVM } from '../../../../core/payments-common.js';
import { CurrencyUIConfiguration } from '../../../../core/payments-common.js';
import { DecimalPipe } from '../../../../pipes/decimal.pipe.js';
import { AnalyticsTrackingService } from '../../../../core/analytics-tracking.service.js';

interface CryptoForm {
  ticker: FormControl<string | null>;
  currency: FormControl<string | null>;
}

interface TickerVM extends CurrencyUIConfiguration {
  ticker: string;
  sort_order: number;
  display_name: string;
  is_multi_network: boolean;
  currencies: CurrencyVM[];
}

@Component({
  selector: 'app-crypto-purchase',
  templateUrl: './crypto-purchase.component.html',
  styleUrls: ['../styles.scss', './crypto-purchase.component.scss'],
  imports: [
    CardComponent,
    CardContentComponent,
    PurchaseDialogHeaderComponent,
    MatOption,
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatSelectModule,
    MatInput,
    QRCodeComponent,
    IconComponent,
    CdkCopyToClipboard,
    LoadingComponent,
    PurchaseReceiptComponent,
    DecimalPipe,
  ],
})
export class CryptoPurchaseComponent {
  // Service Injections
  private readonly _activePurchaseViewService = inject(ActivePurchaseViewService);
  private readonly _exchangeRates = inject(ExchangeRatesService);
  private readonly _paymentMethods = inject(PaymentMethodsService);
  private readonly _analytics = inject(Analytics);
  private readonly _userView = inject(UserViewService);
  private readonly _logger = inject(LOGGER_FACTORY).create('CryptoPurchase');
  private readonly _currencies = inject(CurrenciesViewService);
  private readonly _analyticsTracking = inject(AnalyticsTrackingService);
  alertService = inject(AlertService);

  // Input/Output Properties
  package = input.required<PurchaseSelection>();
  readonly setPaymentMethod = output<PurchasePaymentMethod>();
  readonly back = output<void>();
  readonly close = output<void>();
  readonly purchaseLimitReached = output<void>();

  // Form Controls
  cryptoForm = new FormGroup<CryptoForm>({
    ticker: new FormControl<string | null>(null),
    currency: new FormControl<string | null>(null),
  });

  // Observable to Signal Conversions
  currencies = toSignal(this._currencies.currencies$);
  user = toSignal(this._userView.state$);
  private _currencyFormValue = toSignal(this.cryptoForm.controls.currency.valueChanges);
  private _tickerFormValue = toSignal(this.cryptoForm.controls.ticker.valueChanges);

  // Computed Signals
  tickers = computed(() =>
    Object.values(
      this.currencies()
        ?.filter((c) => c.is_cryptocurrency)
        .reduce(
          (acc, c) => {
            let sort_order: number | undefined;

            switch (c.ticker) {
              case 'BTC':
                sort_order = 0;
                break;
              case 'USDC':
                sort_order = 1;
                break;
              case 'SOL':
                sort_order = 2;
                break;
              case 'ETH':
                sort_order = 3;
                break;
              case 'USDT':
                sort_order = 4;
                break;
            }

            acc[c.ticker] = acc[c.ticker] || {
              ticker: c.ticker,
              ui_id: c.ui_id,
              ui_name: c.ui_name,
              ui_label: c.ui_label,
              ui_decimals: c.ui_decimals,
              ui_icon: c.ui_icon,
              display_name: c.display_name,
              currencies: [],
              is_multi_network: c.is_multi_network,
              sort_order,
            };

            acc[c.ticker].currencies.push(c);

            return acc;
          },
          {} as Record<string, TickerVM>,
        ) ?? [],
    )
      .filter((c) => typeof c.sort_order === 'number')
      .sort((a, b) => a.sort_order - b.sort_order),
  );
  ticker = computed(() =>
    this._tickerFormValue() ? this.tickers().find((c) => c.ticker === this._tickerFormValue()) : null,
  );
  availableCurrencies = computed(() =>
    this.ticker() ? this.ticker()!.currencies.filter((n) => n.enable_purchases) : [],
  );
  currency = computed(() =>
    this.ticker() && (this.availableCurrencies().length > 1 || this.ticker()!.is_multi_network)
      ? this._currencyFormValue()
        ? this.availableCurrencies().find((n: CurrencyVM) => n.currency_id === this._currencyFormValue())
        : null
      : this.ticker()?.currencies[0],
  );
  showAddresses = computed(() => this.ticker() && (this.ticker()!.currencies.length <= 1 || this.currency()));
  amount = computed(() => this.package()?.inputAmount?.toNumber() ?? 0);

  // Reactive Signals
  addresses = signal<{ address: string; label: string }[] | null>(null);
  convertedAmount = signal<number | null>(null);
  addressCreateLoading = signal(false);
  showQrCode = signal(false);

  goBack() {
    this._activePurchaseViewService.clearActivePurchasePayment();
    this.back.emit();
  }

  constructor() {
    // Latest-wins on currency change: switchMap drops a stale in-flight load if
    // the user picks a different currency before the previous one resolves, so
    // stale addresses/converted-amount can never be applied. The address reset
    // and loading flag still fire on every currency change exactly as before.
    toObservable(this.currency)
      .pipe(
        tap(() => this.addresses.set(null)),
        switchMap((currency) => {
          if (!currency) {
            return EMPTY;
          }

          this.addressCreateLoading.set(true);

          return from(this._loadAddresses(currency)).pipe(
            tap((result) => {
              this.convertedAmount.set(result.convertedAmount);

              const hasHadPurchase = this.user()?.has_had_purchase;

              if (!hasHadPurchase) {
                this._analytics.trackEvent('ftp_wallet_added', {
                  value: this.amount(),
                  currency: 'USD',
                  provider: 'fireblocks',
                });
              }

              this._activePurchaseViewService.setActivePurchasePaymentEntity(
                result.paymentMethods.map((pm) => ({
                  payment_method_id: pm.payment_method_id,
                })),
              );

              this.addresses.set(result.addresses);
            }),
            catchError((err: any) => {
              if (isPurchaseLimitReachedError(err)) {
                this.purchaseLimitReached.emit();
                return EMPTY;
              }
              if (isModeRestrictedError(err)) {
                return EMPTY;
              }

              this.alertService.notifyDanger('Error selecting currency', err.message);
              this._logger.error(`Error selecting currency: ${err.message}`, {
                currency_id: currency.currency_id,
              });
              return EMPTY;
            }),
            finalize(() => this.addressCreateLoading.set(false)),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  private async _loadAddresses(currency: CurrencyVM) {
    const paymentMethodCurrencyId = currency.currency_id;

    let paymentMethods: PaymentMethodAndUserBlockchainAddress[] = (
      await this._paymentMethods.getPaymentMethods({
        entityTypes: [PaymentMethodEntityTypeId.FireblocksVaultAccount],
      })
    ).filter((pm) => pm.currency_id === paymentMethodCurrencyId);

    if (!paymentMethods.length) {
      paymentMethods = await this._paymentMethods.createFireblocksPurchaseAddresses(paymentMethodCurrencyId);
    }

    const convertedAmount = await this._exchangeRates.convertCurrency(
      this.amount(),
      CurrencyId.USD,
      paymentMethodCurrencyId,
    );

    const addresses = paymentMethods
      .filter(
        (pm) =>
          pm.currency_id === paymentMethodCurrencyId &&
          pm.payment_method_entity_type_id === PaymentMethodEntityTypeId.FireblocksVaultAccount &&
          pm.user_blockchain_address_format_id !== UserBlockchainAddressFormatId.BitcoinLegacy,
      )
      .map((pm) => {
        const currencyName =
          pm.user_blockchain_address_format_id === UserBlockchainAddressFormatId.BitcoinSegwit
            ? 'Bitcoin SegWit'
            : pm.user_blockchain_address_format_id === UserBlockchainAddressFormatId.BitcoinLegacy
              ? 'Bitcoin Legacy'
              : currency.ui_name;

        const label = currency.ui_label ? `${currency.ui_label} (${currencyName}) Address` : `${currencyName} Address`;

        return {
          address: pm.payment_method_entity_id,
          label,
        };
      });

    return { paymentMethods, convertedAmount, addresses };
  }
}
